import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, MapPin, Check } from 'lucide-react';
import { boardPublicAPI, fmtSpan } from '@/lib/bulletinAPI';
import { PublicShell } from './PublicBoard';

// A volunteer signs up for one or more shifts. No account, just a name and email.
export default function PublicSignup() {
  const { slug, id } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [picked, setPicked] = useState([]);
  const [f, setF] = useState({ name: '', email: '', phone: '', note: '', newsletter: false, website: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [result, setResult] = useState(null);
  const load = () => boardPublicAPI.event(slug, id).then((r) => { setData(r.data); document.title = r.data.title; }).catch(() => setMissing(true));
  useEffect(() => { load(); }, [slug, id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <PublicShell title="Sign-up"><div className="rounded-xl border bg-white p-6" style={{ borderColor: '#E3DDD1' }}>This sign-up isn't available.</div></PublicShell>;
  if (!data) return <PublicShell title=""><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div></PublicShell>;

  const back = <Link to={`/board/${slug}`} className="text-sm font-semibold text-[#A62B1F]">← Back to the notice board</Link>;

  if (result) {
    return (
      <PublicShell title={data.company} logo={data.logo_url}>
        <div className="rounded-xl border bg-white p-6 space-y-3" style={{ borderColor: '#E3DDD1' }}>
          {result.joined.length > 0 ? (
            <>
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ background: '#A62B1F' }}><Check className="w-6 h-6" /></div>
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Thank you, {f.name.split(' ')[0]}!</h2>
              <p className="text-base">You're signed up for {result.joined.join(', ')}. We've emailed you the details, and we'll send a reminder the day before.</p>
            </>
          ) : <h2 className="text-xl font-semibold">Nothing new was booked</h2>}
          {result.already?.length > 0 && <p className="text-base">You were already signed up for {result.already.join(', ')}.</p>}
          {result.full?.length > 0 && <p className="text-base text-[#A62B1F]">Sorry, {result.full.join(', ')} filled up just before you. Please pick another shift.</p>}
          <div className="flex gap-4 pt-2">{result.full?.length > 0 && <button className="text-sm font-semibold text-[#A62B1F]" onClick={() => { setResult(null); setPicked([]); load(); }}>Pick another shift</button>}{back}</div>
        </div>
      </PublicShell>
    );
  }

  const toggle = (sid) => setPicked(picked.includes(sid) ? picked.filter((x) => x !== sid) : [...picked, sid]);
  const input = 'w-full h-12 rounded-lg border px-3 text-base bg-white';

  return (
    <PublicShell title={data.company} logo={data.logo_url}>
      {back}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{data.title}</h1>
        {data.location && <div className="text-base text-[#5C6670] mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{data.location}</div>}
        {data.description && <p className="text-base leading-relaxed mt-3 whitespace-pre-line">{data.description}</p>}
      </div>

      {!data.open ? <div className="rounded-xl border bg-white p-5 text-base" style={{ borderColor: '#E3DDD1' }}>Sign-ups for this are closed. Thank you for your interest!</div> : (
        <form className="space-y-5" onSubmit={async (e) => {
          e.preventDefault();
          if (!picked.length) return setMsg('Please pick at least one shift.');
          setBusy(true); setMsg('');
          try { const r = await boardPublicAPI.join(slug, id, { ...f, shift_ids: picked }); setResult(r.data); window.scrollTo(0, 0); } catch (er) { setMsg(er?.response?.data?.detail || 'Something went wrong. Please try again.'); } finally { setBusy(false); }
        }}>
          <section>
            <h2 className="text-lg font-semibold mb-2">1. Choose your shift{data.shifts.length > 1 ? 's' : ''}</h2>
            <div className="space-y-2">
              {data.shifts.filter((s) => !s.past).map((s) => {
                const full = s.remaining <= 0;
                const on = picked.includes(s.id);
                return (
                  <button type="button" key={s.id} disabled={full} onClick={() => toggle(s.id)}
                    className="w-full text-left rounded-xl border p-4 flex items-center gap-3 disabled:opacity-50"
                    style={{ borderColor: on ? '#A62B1F' : '#E3DDD1', background: on ? '#FFF7F5' : 'white', borderWidth: on ? 2 : 1 }}>
                    <span className="w-6 h-6 rounded border flex items-center justify-center shrink-0" style={{ borderColor: on ? '#A62B1F' : '#C9C2B4', background: on ? '#A62B1F' : 'white' }}>{on && <Check className="w-4 h-4 text-white" />}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-base">{s.label}</span>
                      <span className="block text-sm text-[#5C6670]">{fmtSpan(s.starts_at, s.ends_at)}</span>
                    </span>
                    <span className="text-sm font-semibold shrink-0" style={{ color: full ? '#5C6670' : '#A62B1F' }}>{full ? 'Full' : `${s.remaining} open`}</span>
                  </button>
                );
              })}
            </div>
          </section>
          <section className="space-y-2">
            <h2 className="text-lg font-semibold">2. Your details</h2>
            <input className={input} style={{ borderColor: '#E3DDD1' }} placeholder="Your name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <input className={input} style={{ borderColor: '#E3DDD1' }} placeholder="Email address" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <input className={input} style={{ borderColor: '#E3DDD1' }} placeholder="Phone (optional)" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
            <textarea className="w-full rounded-lg border p-3 text-base bg-white" style={{ borderColor: '#E3DDD1' }} rows={2} placeholder="Anything we should know? (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
            <label className="flex items-start gap-3 text-base py-1"><input type="checkbox" className="w-5 h-5 mt-0.5" checked={f.newsletter} onChange={(e) => setF({ ...f, newsletter: e.target.checked })} />Also send me the newsletter</label>
          </section>
          {msg && <p className="text-base text-[#A62B1F]">{msg}</p>}
          <button disabled={busy} className="w-full h-14 rounded-xl text-white font-semibold text-lg" style={{ background: '#A62B1F' }}>{busy ? 'Signing you up…' : picked.length > 1 ? `Sign me up for ${picked.length} shifts` : 'Sign me up'}</button>
        </form>
      )}
    </PublicShell>
  );
}
